import React, { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store';
import { useToastContext } from '@/contexts/ToastContext';
import { CreateTransactionData, Transaction } from '@/types';
import { AlertTriangle, ArrowLeft, CheckCircle, ChevronRight, Clock, Coins, Heart, MessageCircle, Phone, Receipt, RefreshCw, Send, User, X, XCircle } from 'lucide-react';

interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  number: string;
  accountName: string;
}

interface PaymentConfirmation {
  phoneNumber: string;
  amount: number;
  name: string;
}

const LIPA_NUMBER = '68256127';
const LIPA_NAME = 'MIPT Software';

const SUPPORT_WHATSAPP = 'https://wa.me/255614021404';
const SUPPORT_PHONE = 'tel:+255614021404';

const transactionStatusIcon = (status: string) => {
  if (status === 'APPROVED') return <CheckCircle className="h-5 w-5 text-green-600" />;
  if (status === 'REJECTED') return <XCircle className="h-5 w-5 text-red-600" />;
  return <Clock className="h-5 w-5 text-yellow-600" />;
};

const historyStatusBadge = (status: string) => {
  const map: Record<string, string> = {
    APPROVED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium ${map[status] || 'bg-yellow-100 text-yellow-700'}`}>
      {status}
    </span>
  );
};

const BillingPage: React.FC = () => {
  const { userBalance, profile, fetchUserBalance, fetchPaymentInfo, createTransaction, transactions, fetchTransactions } = useAppStore();
  const { showSuccess, showError } = useToastContext();

  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentConfirmation>({
    phoneNumber: profile?.phone_number || '',
    amount: 2000,
    name: '',
  });
  const [confirmedPayment, setConfirmedPayment] = useState(false);
  const [showConfirmReminderModal, setShowConfirmReminderModal] = useState(false);
  const [calculatorAmount, setCalculatorAmount] = useState<number>(2000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionSuccess, setTransactionSuccess] = useState<{
    show: boolean;
    transactionId?: number;
    amount?: number;
  }>({ show: false });

  useEffect(() => {
    fetchUserBalance().catch(console.error);
    fetchPaymentInfo().catch(console.error);
    // Update phone number when profile loads
    if (profile?.phone_number) {
      setPaymentDetails(prev => ({ ...prev, phoneNumber: profile.phone_number || '' }));
    }
  }, [fetchUserBalance, fetchPaymentInfo, profile?.phone_number]);

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'mpesa',
      name: 'M-Pesa',
      description: 'LiPA NAMBA',
      icon: <Phone className="h-6 w-6" />,
      number: LIPA_NUMBER,
      accountName: LIPA_NAME,
    },

  ];

  const calculatedTokens = useMemo(() => Math.floor(calculatorAmount * 0.5), [calculatorAmount]);

  // Value messaging: an AI-enhanced report export costs 200 tokens.
  // Keep the copy to whole numbers (truncated).
  const valueStats = useMemo(() => ({
    enhancedExports: Math.floor(calculatedTokens / 200),
  }), [calculatedTokens]);

  const loadHistory = React.useCallback(async () => {
    setHistoryLoading(true);
    try {
      await fetchTransactions();
    } catch (error) {
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  }, [fetchTransactions]);

  React.useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionSuccess.show]);

  const handleProceedToPayment = () => {
    if (calculatorAmount < 1000) {
      showError('Minimum payment amount is 1000 TSH');
      return;
    }

    if (calculatorAmount % 500 !== 0) {
      showError('Payment amount must be in multiples of 500 TSH');
      return;
    }

    setTransactionSuccess({ show: false });
    setConfirmedPayment(false);
    setPaymentDetails((prev) => ({
      ...prev,
      amount: calculatorAmount,
    }));
    setShowPaymentMethods(true);
  };

  const performSubmit = async () => {
    setIsSubmitting(true);
    try {
      const transactionData: CreateTransactionData = {
        user_phone_number: paymentDetails.phoneNumber,
        sender_name: paymentDetails.name,
        payment_method: 'DIRECT',
        amount: paymentDetails.amount,
      };

      const transaction = await createTransaction(transactionData);

      showSuccess('Transaction submitted successfully. Staff will verify your payment and add tokens to your account.');
      setTransactionSuccess({
        show: true,
        transactionId: transaction.id,
        amount: parseFloat(transaction.amount) || paymentDetails.amount,
      });
      setShowPaymentMethods(false);
      setCalculatorAmount(2000);
      setPaymentDetails({
        phoneNumber: profile?.phone_number || '',
        amount: 2000,
        name: '',
      });
      setConfirmedPayment(false);
      fetchUserBalance().catch(console.error);
    } catch (error) {
      console.error('Transaction creation error:', error);
      showError('Failed to submit transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitConfirmation = () => {
    if (!paymentDetails.amount || paymentDetails.amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (!paymentDetails.phoneNumber || !paymentDetails.phoneNumber.trim()) {
      showError('Please enter the phone number used for payment');
      return;
    }

    if (!paymentDetails.name.trim()) {
      showError('Please enter the sender name');
      return;
    }

    if (!confirmedPayment) {
      // Remind the user to send the donation to the LIPA NAMBA first.
      setShowConfirmReminderModal(true);
      return;
    }

    void performSubmit();
  };

  const handleReminderModalConfirm = () => {
    if (!confirmedPayment) {
      // Defensive: the button is disabled until confirmed, but keep the guard.
      return;
    }
    setShowConfirmReminderModal(false);
    void performSubmit();
  };

  return (
    <>
      <div className="mx-auto max-w-4xl p-4 lg:p-6">
        {transactionSuccess.show ? (
          <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-500 p-1 shadow-lg">
              <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-500">
                <Heart className="h-12 w-12 fill-white text-white" />
              </div>
            </div>
            <h1 className="mt-8 text-2xl font-bold text-gray-900 lg:text-3xl">
              Thank you for your Donation!
            </h1>
            <p className="mt-3 max-w-md text-sm text-gray-600 lg:text-base">
              It means a lot to us. Your support helps us provide better service and reach out to
              more people.
            </p>
            {transactionSuccess.amount ? (
              <p className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-2 text-sm text-gray-700">
                {transactionSuccess.amount.toLocaleString()} TSH donation received
                {transactionSuccess.transactionId
                  ? <> · Transaction #{transactionSuccess.transactionId}</>
                  : null}{' '}
                — pending verification.
              </p>
            ) : null}
            <button
              onClick={() => setTransactionSuccess({ show: false })}
              className="mt-8 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-orange-600 px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700"
            >
              <Coins className="h-4 w-4" />
              Back to Billing
            </button>

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-3">Need quick confirmation? Contact our support team:</p>
              <div className="flex justify-center gap-3">
                <a
                  href="https://wa.me/255614021404?text=Hi! I need quick confirmation for my donation."
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-green-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Support
                </a>
                <a
                  href="tel:+255614021404"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700"
                >
                  <Phone className="h-4 w-4" />
                  Call Support
                </a>
              </div>
            </div>
          </div>
        ) : (
          <>
            {!showPaymentMethods ? (
              <div className="mb-6 rounded-3xl border border-orange-100 bg-gradient-to-br from-white to-orange-50/50 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
                    <Heart className="h-6 w-6" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900 lg:text-3xl">Support & Sustainability</h1>
                    <p className="mt-2 max-w-2xl text-sm text-gray-600 lg:text-base">
                      Your donation helps this platform, enabling students without using Laptop to prepare weekly report
                      (Logbook). Let&apos;s Be Love ❤️
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mb-6 hidden gap-4 md:grid md:grid-cols-3">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Your Tokens</p>
                <div className="mt-2 flex items-center gap-2 text-gray-900">
                  <Coins className="h-5 w-5 text-orange-600" />
                  <span className="text-2xl font-semibold">{userBalance?.available_tokens ?? 0}</span>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Minimum Donation</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">1,000 TSH</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Tokens in Action</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">1,000 tokens</p>
                <p className="text-xs text-gray-500">
                  = 5 fully AI-enhanced reports
                </p>
              </div>
            </div>

            {!showPaymentMethods ? (
              <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm lg:p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                    <Coins className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 lg:text-lg">Token Calculator</h2>
                    <p className="text-sm text-gray-600">Calculate how many tokens you'll receive for your donation.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Donation Amount (TSH)</label>
                    <input
                      type="number"
                      value={calculatorAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const cleanValue = value.replace(/^0+/, '') || '0';
                        setCalculatorAmount(Number(cleanValue));
                      }}
                      min="1000"
                      className="input-field input-field-sm"
                      placeholder="Enter donation amount"
                    />
                    <p className="mt-1 text-xs text-gray-500">Minimum: 1,000 TSH</p>
                  </div>

                  <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900 lg:text-xl">{calculatedTokens}</div>
                    <div className="text-xs text-gray-600">Tokens</div>
                    <div className="mt-2 space-y-1 px-2 text-xs text-gray-600">
                      {valueStats.enhancedExports >= 1 ? (
                        <p className="leading-relaxed">
                          Enough for{' '}
                          <span className="font-bold text-orange-700">{valueStats.enhancedExports}</span>{' '}
                          fully AI-enhanced {valueStats.enhancedExports === 1 ? 'report' : 'reports'}.
                        </p>
                      ) : null}
                      <p className="leading-relaxed text-gray-500">
                        Add a diagram to any export to strengthen your logbook.
                      </p>
                    </div>
                  </div>
                </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={handleProceedToPayment}
                    className="w-full rounded-2xl bg-orange-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700"
                  >
                    Proceed to Donation
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Submit Payment Details</h2>
                    <p className="mt-1 text-sm text-gray-600">Enter your payment information for verification.</p>
                  </div>
                  <button
                    onClick={() => setShowPaymentMethods(false)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                </div>

                <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  <img src="/mpesa.png" alt="M-Pesa" className="w-full object-contain" />
                </div>

                <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-orange-600 shadow-sm">
                          {method.icon}
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{method.description || method.name}</h4>
                          <p className="text-xs text-gray-500">Payment Number</p>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between rounded-xl bg-white px-3 py-2">
                          <span className="text-gray-500">Number</span>
                          <span className="font-semibold text-gray-900">{method.number}</span>
                        </div>
                        <div className="flex items-center justify-between rounded-xl bg-white px-3 py-2">
                          <span className="text-gray-500">Name</span>
                          <span className="font-semibold text-gray-900">{method.accountName}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-5 rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm text-gray-700">
                  Amount selected: <span className="font-semibold text-gray-900">{calculatorAmount.toLocaleString()} TSH</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Phone number used</label>
                    <input
                      type="tel"
                      value={paymentDetails.phoneNumber}
                      onChange={(e) => setPaymentDetails((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                      className="input-field"
                      placeholder="Enter phone number used for payment"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Sender name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={paymentDetails.name}
                        onChange={(e) => setPaymentDetails((prev) => ({ ...prev, name: e.target.value }))}
                        className="input-field pl-10"
                        placeholder="Enter the name on the sending number"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Amount sent (TSH)</label>
                    <input
                      type="number"
                      value={paymentDetails.amount}
                      onChange={(e) => setPaymentDetails((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                      min="1000"
                      className="input-field"
                      placeholder="Enter amount sent"
                    />
                  </div>

                  <label
                    className="flex cursor-pointer items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4 transition-colors hover:bg-orange-100/60"
                  >
                    <input
                      type="checkbox"
                      checked={confirmedPayment}
                      onChange={(e) => setConfirmedPayment(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-orange-600 accent-orange-600"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm I have sent the donation of{' '}
                      <span className="font-semibold text-gray-900">{paymentDetails.amount ? paymentDetails.amount.toLocaleString() : calculatorAmount.toLocaleString()} TSH</span>{' '}
                      to the LIPA NAMBA above ({LIPA_NUMBER} – {LIPA_NAME}).
                    </span>
                  </label>

                  <button
                    onClick={handleSubmitConfirmation}
                    disabled={isSubmitting}
                    className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Send className="h-4 w-4" />
                    {isSubmitting ? 'Submitting...' : 'Submit Transaction'}
                  </button>
                </div>
              </div>
            )}

            {!showPaymentMethods && (
              <div className="mt-6 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm lg:p-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                      <Receipt className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 lg:text-lg">Transaction History</h2>
                      <p className="text-sm text-gray-600">Track your payments and their status.</p>
                    </div>
                  </div>
                  <button
                    onClick={loadHistory}
                    disabled={historyLoading}
                    className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {transactions.length === 0 ? (
                  <div className="py-10 text-center">
                    <Receipt className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                    <p className="text-sm text-gray-500">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((tx) => (
                      <button
                        key={tx.id}
                        onClick={() => setSelectedTransaction(tx)}
                        className="flex w-full items-center gap-3 rounded-2xl border border-gray-100 p-3 text-left transition-colors hover:bg-gray-50"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-50">
                          {transactionStatusIcon(tx.transaction_status)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {parseFloat(tx.amount || '0').toLocaleString()} TSH
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(tx.created_at).toLocaleString()}
                            {tx.tokens_generated > 0 && ` • ${tx.tokens_generated} tokens`}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {historyStatusBadge(tx.transaction_status)}
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-orange-100 bg-orange-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">Payment not reflected? Reach out to our support team.</p>
                  <div className="flex gap-2">
                    <a
                      href="https://wa.me/255614021404"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-2xl bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </a>
                    <a
                      href="tel:+255614021404"
                      className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-2xl bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showConfirmReminderModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm donation sent"
        >
          <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <button
              onClick={() => setShowConfirmReminderModal(false)}
              aria-label="Close confirmation dialog"
              className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
              <AlertTriangle className="h-7 w-7" />
            </div>

            <h2 className="mt-4 text-lg font-bold text-gray-900">Confirm your donation</h2>
            <p className="mt-2 text-sm text-gray-600">
              Please make sure you have sent{' '}
              <span className="font-semibold text-gray-900">{paymentDetails.amount.toLocaleString()} TSH</span>{' '}
              to the LIPA NAMBA below before submitting:
            </p>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">LIPA NAMBA</span>
                <span className="font-bold text-gray-900">{LIPA_NUMBER}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-gray-500">Name</span>
                <span className="font-semibold text-gray-900">{LIPA_NAME}</span>
              </div>
            </div>

            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4 transition-colors hover:bg-orange-100/60">
              <input
                type="checkbox"
                checked={confirmedPayment}
                onChange={(e) => setConfirmedPayment(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-orange-600 accent-orange-600"
              />
              <span className="text-sm text-gray-700">
                I confirm I have sent the donation to the LIPA NAMBA above.
              </span>
            </label>

            <button
              onClick={handleReminderModalConfirm}
              disabled={isSubmitting || !confirmedPayment}
              className="mt-5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {isSubmitting ? 'Submitting...' : 'Confirm & Submit Transaction'}
            </button>
          </div>
        </div>
      )}

      {selectedTransaction && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Transaction details"
        >
          <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <button
              onClick={() => setSelectedTransaction(null)}
              aria-label="Close details"
              className="absolute right-4 top-4 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                <Receipt className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Transaction #{selectedTransaction.id}</h2>
                <div className="mt-1">{historyStatusBadge(selectedTransaction.transaction_status)}</div>
              </div>
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Amount</span>
                <span className="font-semibold text-gray-900">{parseFloat(selectedTransaction.amount || '0').toLocaleString()} TSH</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Tokens</span>
                <span className="font-medium text-gray-900">{selectedTransaction.tokens_generated}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Payment method</span>
                <span className="font-medium text-gray-900">
                  {selectedTransaction.payment_method}
                  {selectedTransaction.wakala_name ? ` (${selectedTransaction.wakala_name})` : ''}
                </span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Sender name</span>
                <span className="font-medium text-gray-900">{selectedTransaction.sender_name || '-'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium text-gray-900">{selectedTransaction.user_phone_number || '-'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500">Submitted</span>
                <span className="font-medium text-gray-900">{new Date(selectedTransaction.created_at).toLocaleString()}</span>
              </div>
              {selectedTransaction.confirmed_by_name && (
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Confirmed by</span>
                  <span className="font-medium text-gray-900">{selectedTransaction.confirmed_by_name}</span>
                </div>
              )}
            </div>

            {selectedTransaction.transaction_status === 'REJECTED' && (
              <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-500">Reason for rejection</p>
                <p className="mt-1 text-sm text-red-800">{selectedTransaction.note || 'No reason provided.'}</p>
              </div>
            )}

            {selectedTransaction.transaction_status === 'APPROVED' && selectedTransaction.note && (
              <div className="mt-4 rounded-2xl border border-green-100 bg-green-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-green-600">Note</p>
                <p className="mt-1 text-sm text-green-800">{selectedTransaction.note}</p>
              </div>
            )}

            {selectedTransaction.transaction_status === 'PENDING' && (
              <div className="mt-4 flex items-start gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-4">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  Your payment is awaiting staff verification. This usually takes a short while.
                </p>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 p-4">
              <p className="text-sm text-gray-700">
                Need help with this transaction? Our support team is here for you.
              </p>
              <div className="mt-3 flex gap-2">
                <a
                  href={SUPPORT_WHATSAPP}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-2xl bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  href={SUPPORT_PHONE}
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700"
                >
                  <Phone className="h-4 w-4" />
                  Call Support
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BillingPage;